import React from 'react';
import * as yup from 'yup';
import { CircularProgress, Grid } from '@mui/material';
import { BackIcon } from 'components/SVG';
import {
  SparkForm,
  SparkFormImageUploader,
  SparkTextField,
  SparkFormTaggedAutoComplete,
  SparkFormTaggedTextField,
  SparkBasicFormFooter,
  SparkDropdown,
} from 'sharedComponents/SparkForm';
import { useAddMemory } from 'hooks/memory';
import { useGetCategories } from 'hooks/Categories';
import { useFileUploads } from 'hooks/useFileUploads';
import { useStatusBarStore } from 'stores';
import { useOperations } from 'hooks/batchOperations';
import { SPARK_RESOURCE_TYPES } from 'constants/common';
import { AddOperation } from 'hooks/batchOperations/core/useOperations';

type MemoryFormValues = {
  tags: string[];
  imageURL: string;
  description: string;
  name: string;
  subCategories: string[];
  mainCategoryID: string;
};

type CreateMemoryFormProps = {
  handlePreviousClick: () => void;
  onClose: () => void;
  uploadFilesURLs: string[];
  onMemoryAdd?: (id: string) => void;
};

function CreateMemoryForm({
  handlePreviousClick,
  onClose,
  uploadFilesURLs,
  onMemoryAdd,
}: CreateMemoryFormProps) {
  const [mainCategoryID, setMainCategoryID] = React.useState('');
  const { mutateAsync: addMemory, isLoading: isAddingMemory } = useAddMemory();
  const {
    dropdownOptions: mainCategoriesOptions,
    isLoading: isLoadingMainCategories,
  } = useGetCategories(null);

  const {
    dropdownOptions: subCategoriesOptions,
    isLoading: isLoadingSubCategories,
  } = useGetCategories(mainCategoryID, !!mainCategoryID);

  const { mutateAsync: uploadFiles, isLoading: isUloadingFiles } =
    useFileUploads();
  const setStatusConfig = useStatusBarStore((state) => state.setStatusConfig);
  const { mutateAsync: performOperations, isLoading: isPerformingOperations } =
    useOperations();

  const initialValues = {
    tags: [],
    imageURL: '',
    subCategories: [],
    mainCategoryID: '',
    description: '',
    name: '',
  };

  const validationSchema = yup.object({
    name: yup.string().required('Name is Required').max(50),
    description: yup.string().required('Description is Required').max(200),
    mainCategoryID: yup.string().required('Category is Required'),
    subCategories: yup
      .array()
      .min(1, 'Subcategories field must have at least 1 item')
      .required('Subcategories field must have at least 1 item'),
  });

  const handleSubmit = async (formValues: MemoryFormValues) => {
    const { name, imageURL, description, subCategories, mainCategoryID } =
      formValues;

    const memoryResponse = await addMemory({
      name,
      logoURL: imageURL,
      description,
    });

    const addCategoryOperations: AddOperation[] = subCategories.map(
      (categoryID) => ({
        op: 'add',
        data: {
          type: SPARK_RESOURCE_TYPES.MEMORY_CATEGORIES,
          attributes: {
            categoryID: categoryID,
            memoryID: memoryResponse.data.id,
            mainCategory: false,
          },
        },
      })
    );
    const mainCategoryOperation: AddOperation = {
      op: 'add',
      data: {
        type: SPARK_RESOURCE_TYPES.MEMORY_CATEGORIES,
        attributes: {
          mainCategory: true,
          categoryID: mainCategoryID,
          memoryID: memoryResponse.data.id,
        },
      },
    };
    await performOperations([...addCategoryOperations, mainCategoryOperation]);

    await uploadFiles({
      memoryID: memoryResponse.data.id,
      files: uploadFilesURLs.map((fileUrl) => ({ url: fileUrl })),
    });

    setStatusConfig({
      statusBarType: 'memoryCreation',
      statusBarID: memoryResponse.data.id,
    });
    onMemoryAdd && onMemoryAdd(memoryResponse.data.id);

    onClose();
  };

  const isLoading = isLoadingMainCategories;

  const isMutating =
    isUloadingFiles || isAddingMemory || isPerformingOperations;

  if (isLoading) {
    return (
      <Grid container height='100%' justifyContent='center' alignItems='center'>
        <CircularProgress />
      </Grid>
    );
  }
  return (
    <SparkForm<MemoryFormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSave={handleSubmit}
      containerProps={{ columnSpacing: 2 }}
    >
      <SparkFormImageUploader name='imageURL' gridProps={{ mb: 2 }} />
      <SparkTextField
        size={12}
        name='name'
        label='Name'
        showCounter={true}
        maxNumber={50}
        placeholder='Source Name'
      />
      <SparkTextField
        size={12}
        multiline={true}
        name='description'
        label='Short Description'
        placeholder='Please Enter a Short Description'
        showCounter={true}
        maxNumber={200}
      />
      <SparkDropdown
        size={12}
        label='Main Category'
        name='mainCategoryID'
        placeholder='Select Category'
        children={mainCategoriesOptions}
        onChange={(value) => {
          setMainCategoryID(value as string);
        }}
      />
      <SparkFormTaggedAutoComplete
        label='Sub Categories'
        name='subCategories'
        disabled={isLoadingMainCategories}
        placeholder='Select one or more Categories'
        options={subCategoriesOptions}
      />
      <SparkBasicFormFooter
        primaryButtonText='Finish'
        primaryButtonStyles={{ width: 'unset' }}
        shouldRenderResetButton={true}
        resetButtonType='button'
        onResetButtonClick={handlePreviousClick}
        resetButtonText={<BackIcon />}
        resetButtonStyles={{ width: 'unset' }}
        containerProps={{
          sx: { justifyContent: 'space-between', paddingBottom: 0 },
        }}
        isLoading={isMutating}
      />
    </SparkForm>
  );
}

export default CreateMemoryForm;
